// B-70 — the pure half of the panel grouped by date, the first screen of a client whose reports
// cover all its offices at once (Bancamía's three daily files). One line per closing date: how its
// offices stand, and how each of the day's documents arrived. A line opens the panel list
// (`PanelCierres`) on that one date.
//
// Nothing here names a client (B-40's rule for this folder). The backend's `getCierreClosingDates`
// answers, per date, its closings grouped by `state`, whether a daily document was never read for
// them (`missingReport`) and whether they carry an open blocking alert (`blocking`); this file
// words that the way the detail does, so a line's counts are the offices its detail lists.

import { CUADRE_OK_STATUS, fechaDMY } from "./cierrePanel";

// How one office counts on its date's line. Every office lands in exactly one, so
// `ok + alerta + sinReportes + porRevisar === total` always holds.
export const ESTADO_OFICINA = {
  OK: "ok",
  ALERTA: "alerta",
  SIN_REPORTES: "sinReportes",
  POR_REVISAR: "porRevisar",
};

// A day's document, collapsed for its dot.
export const ESTADO_DOCUMENTO = {
  RECIBIDO: "recibido",
  FALTANTE: "faltante",
  ERROR: "error",
};

export const ETIQUETA_DOCUMENTO = {
  recibido: "Recibido",
  faltante: "Faltante",
  error: "Con error",
};

const FALLIDOS = ["ERROR", "TIMEOUT"];
const INTENTOS = ["OK", "EMPTY", "ERROR", "TIMEOUT"];

// The states the client's panel words as balanced (`statuses.byState` from `getExtractionFields`),
// the same word the detail's `estadoDeFila` reads. Without the mapping, the two states the
// lifecycle ends a balanced closing in.
export function estadosDeCuadre(byState) {
  if (!byState) return ["BALANCED", "RESOLVED"];
  return Object.entries(byState)
    .filter(([, etiqueta]) => etiqueta === CUADRE_OK_STATUS)
    .map(([estado]) => estado);
}

// One group of closings (`{ state, missingReport, blocking }`) as the line counts it, in the
// detail's order. A balanced closing is OK whatever else is true. Then a daily document never read
// for the office: "sin reportes", even though the missing report raised a blocking alert of its own —
// otherwise a day nobody processed would read as 140 offices in alert. Then an open blocking alert.
// What is left was read, raised nothing blocking and is not balanced: the detail's "Pendiente
// revisión".
export function estadoDeGrupo(grupo, cuadre) {
  if (cuadre.includes(grupo?.state)) return ESTADO_OFICINA.OK;
  if (grupo?.missingReport) return ESTADO_OFICINA.SIN_REPORTES;
  if (grupo?.blocking) return ESTADO_OFICINA.ALERTA;
  return ESTADO_OFICINA.POR_REVISAR;
}

// One daily document's dot, from its counts over the day's offices. A failure on any office is
// the error to look at; an office the document was never read for means it has not arrived. EMPTY
// is a received file with no rows for an office — a warning per office its closing's alert says.
export function estadoDeDocumento(conteo) {
  if (!conteo) return ESTADO_DOCUMENTO.FALTANTE;
  if (FALLIDOS.some((estado) => (conteo[estado] || 0) > 0)) return ESTADO_DOCUMENTO.ERROR;
  if ((conteo.missing || 0) > 0) return ESTADO_DOCUMENTO.FALTANTE;
  if ((conteo.OK || 0) + (conteo.EMPTY || 0) > 0) return ESTADO_DOCUMENTO.RECIBIDO;
  return ESTADO_DOCUMENTO.FALTANTE;
}

// The day's documents in the order the backend lists them, each with its dot.
export function documentosDelDia(documents) {
  return Object.entries(documents || {}).map(([clave, conteo]) => ({ clave, estado: estadoDeDocumento(conteo) }));
}

// Nobody processed the day: not one daily document was ever tried on any of its offices. Such a
// day has nothing to count, only something to do.
export function diaSinProcesar(documents) {
  const conteos = Object.values(documents || {});
  return conteos.length > 0 && conteos.every((conteo) => INTENTOS.every((estado) => !(conteo?.[estado] > 0)));
}

// One date of `getCierreClosingDates` as its line.
export function resumenDeFecha(dia, byState) {
  const cuadre = estadosDeCuadre(byState);
  const conteo = { ok: 0, alerta: 0, sinReportes: 0, porRevisar: 0 };
  (dia?.groups || []).forEach((grupo) => {
    conteo[estadoDeGrupo(grupo, cuadre)] += grupo.closings || 0;
  });
  return {
    fecha: dia.businessDate,
    fechaLabel: fechaDMY(dia.businessDate),
    total: dia.closings || 0,
    ...conteo,
    documentos: documentosDelDia(dia.documents),
    sinProcesar: diaSinProcesar(dia.documents),
  };
}
